import React, { useState, useEffect } from "react";
import useAPI from "../hooks/use-api";
import useAccessToken from "../hooks/use-accesstoken";
import "../styles/DocumentPanel.css";
import { useTheme } from "@emotion/react";
import { 
    Box, 
    Typography, 
    Button, 
    TextField, 
    IconButton, 
    List, 
    ListItem, 
    ListItemText, 
    ListItemSecondaryAction,
    CircularProgress,
    Paper,
    Divider
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const DocumentPanel = ({ onDelete, onUpload }) => {
    const theme = useTheme();
    const { api } = useAPI();
    const { accessToken } = useAccessToken(); 

    const [documents, setDocuments] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        async function fetchDocumentsWithRetry(attempt = 1, maxRetries = 3) {
            if (!accessToken) {
                console.warn("No access token available. Retrying...");
                if (attempt <= maxRetries) {
                    setTimeout(() => fetchDocumentsWithRetry(attempt + 1, maxRetries), 1000 * attempt);
                }
                return;
            }
    
            setLoading(true);
            try {
                const response = await api.get("/get_documents");
                setDocuments(response.data);
            } catch (error) {
                console.error(`Error fetching documents (Attempt ${attempt}): ${error}`);
    
                if (error.includes("no accesstoken provided") && attempt < maxRetries) {
                    console.warn(`Retrying fetchDocuments in ${attempt} second(s)...`);
                    setTimeout(() => fetchDocumentsWithRetry(attempt + 1, maxRetries), 1000 * attempt);
                }
            } finally {
                setLoading(false);
            }
        }
    
        fetchDocumentsWithRetry();
    }, [accessToken]);

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
    };

    const handleUpload = async () => {
        if (!selectedFile) return;
        setLoading(true);
        setUploadStatus("Uploading file...");

        try {
            const fileContent = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = (error) => reject(error);
                reader.readAsArrayBuffer(selectedFile);
            });
    
            const fileData = {
                filename: selectedFile.name,
                file_content: Array.from(new Uint8Array(fileContent))
            };
    
            const response = await api.post("/upload_documents", fileData);
            const newDocument = response.data.newfile;
    
            setDocuments((prev) => [newDocument, ...prev]);
    
        } catch (error) {
            console.error("Error uploading file:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (filename) => {
        setLoading(true);
        try {
            await api.post("/delete_document", { filename });
            setDocuments((prev) => prev.filter((doc) => doc !== filename));
        } catch (error) {
            console.error("Error deleting file:", error);
        } finally {
            setLoading(false);
        }
    };

    // Filter documents based on the search query (case insensitive)
    const filteredDocuments = documents.filter((doc) =>
        doc.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <Box sx={{ 
            display: "flex", 
            flexDirection: "column", 
            height: "100%",
            width: "100%",
        }}>
            {loading && (
                <Box sx={{ 
                    position: "absolute", 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    display: "flex", 
                    justifyContent: "center", 
                    alignItems: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    zIndex: 10,
                    borderRadius: "8px"
                }}>
                    <CircularProgress sx={{ color: theme.palette.primary.main }} />
                </Box>
            )}
            
            {uploadStatus && (
                <Typography 
                    variant="body2" 
                    sx={{ 
                        mb: 2, 
                        color: theme.palette.primary.dark,
                        fontWeight: 500
                    }}
                >
                    {uploadStatus}
                </Typography>
            )}
            
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 2, 
                    mb: 2, 
                    backgroundColor: `${theme.palette.primary.main}10`,
                    borderRadius: "8px",
                    border: `1px solid ${theme.palette.primary.main}30`
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Button
                        component="label"
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        sx={{ 
                            mr: 1,
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.dark,
                            "&:hover": {
                                borderColor: theme.palette.primary.dark,
                                backgroundColor: `${theme.palette.primary.main}10`
                            }
                        }}
                    >
                        Välj fil
                        <input
                            type="file"
                            hidden
                            onChange={handleFileChange}
                            disabled={loading}
                        />
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={loading || !selectedFile}
                        startIcon={<CloudUploadIcon />}
                        sx={{ 
                            backgroundColor: theme.palette.primary.main,
                            "&:hover": {
                                backgroundColor: theme.palette.primary.dark
                            }
                        }}
                    >
                        Ladda upp
                    </Button>
                </Box>
                
                {selectedFile && (
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: theme.palette.primary.dark,
                            fontStyle: "italic"
                        }}
                    >
                        Vald fil: {selectedFile.name}
                    </Typography>
                )}
            </Paper>
            
            <Box sx={{ 
                position: "relative", 
                mb: 2 
            }}>
                <TextField
                    fullWidth
                    placeholder="Sök efter dokument..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    disabled={loading}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#fff",
                            "& fieldset": {
                                borderColor: `${theme.palette.primary.main}50`,
                            },
                            "&:hover fieldset": {
                                borderColor: theme.palette.primary.main,
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: theme.palette.primary.main,
                            }
                        }
                    }}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />,
                    }}
                />
            </Box>
            
            <Paper 
                elevation={0} 
                sx={{ 
                    flex: 1, 
                    overflow: "auto",
                    borderRadius: "8px",
                    border: `1px solid ${theme.palette.primary.main}30`,
                    backgroundColor: "#fff",
                    maxHeight: "calc(100vh - 300px)",
                    overflowY: "auto"
                }}
            >
                <List sx={{ p: 0 }}>
                    {filteredDocuments.length > 0 ? (
                        filteredDocuments.map((doc, index) => {
                            const truncatedName = doc.length > 40 ? doc.substring(0, 35) + "..." : doc;
                            return (
                                <React.Fragment key={doc}>
                                    <ListItem 
                                        sx={{ 
                                            py: 1.5,
                                            "&:hover": {
                                                backgroundColor: `${theme.palette.primary.main}05`
                                            }
                                        }}
                                    >
                                        <ListItemText 
                                            primary={truncatedName} 
                                            primaryTypographyProps={{
                                                sx: { 
                                                    color: theme.palette.primary.dark,
                                                    fontWeight: 500
                                                }
                                            }}
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton 
                                                edge="end" 
                                                onClick={() => handleDelete(doc)}
                                                disabled={loading}
                                                sx={{ 
                                                    color: theme.palette.error.main,
                                                    "&:hover": {
                                                        backgroundColor: `${theme.palette.error.main}10`
                                                    }
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                    {index < filteredDocuments.length - 1 && (
                                        <Divider sx={{ mx: 2, borderColor: `${theme.palette.primary.main}20` }} />
                                    )}
                                </React.Fragment>
                            );
                        })
                    ) : (
                        <ListItem>
                            <ListItemText 
                                primary="Inga dokument hittades" 
                                primaryTypographyProps={{
                                    sx: { 
                                        color: theme.palette.text.secondary,
                                        textAlign: "center",
                                        fontStyle: "italic"
                                    }
                                }}
                            />
                        </ListItem>
                    )}
                </List>
            </Paper>
        </Box>
    );
};

export default DocumentPanel;
